import { Directive, ElementRef, forwardRef, HostListener, inject, Input, Renderer2 } from "@angular/core";
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from "@angular/forms";

/** TODO: This class should be imported from `@angular/forms` once we have upgraded to `@angular/forms@^8.0.0`. */
class NumberValueAccessor implements ControlValueAccessor {
	/** The registered callback function called when a change or input event occurs on the input element. */
	onChange = (_: any) => {}; // eslint-disable-line @typescript-eslint/no-empty-function

	/** The registered callback function called when a blur event occurs on the input element. */
	onTouched = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function

	constructor(
		private renderer: Renderer2,
		private elementRef: ElementRef,
	) {}

	/**
	 * Sets the "value" property on the input element.
	 *
	 * @param value The checked value.
	 */
	writeValue(value: number): void {
		// The value needs to be normalized for IE9, otherwise it is set to 'null' when null
		const normalizedValue = value === null ? "" : value;
		this.renderer.setProperty(this.elementRef.nativeElement, "value", normalizedValue);
	}

	/**
	 * Registers a function called when the control value changes.
	 *
	 * @param fn The callback function.
	 */
	registerOnChange(fn: (_: number | null) => void) {
		this.onChange = (value) => {
			if (value !== null && !Number.isNaN(value)) {
				fn(value === "" ? null : parseFloat(value));
			} else {
				fn(value);
			}
		};
	}

	/**
	 * Registers a function called when the control is touched.
	 *
	 * @param fn The callback function.
	 */
	registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	/**
	 * Sets the "disabled" property on the input element.
	 *
	 * @param isDisabled The disabled value.
	 */
	setDisabledState(isDisabled: boolean): void {
		this.renderer.setProperty(this.elementRef.nativeElement, "disabled", isDisabled);
	}
}

const CONTROL_KEYS = new Set(["Up", "ArrowUp", "Down", "ArrowDown"]);

const isNumber = (input: any): boolean => typeof input === "number";

interface Number2DirectiveValidationErrors {
	invalidNumber?: boolean;
	greaterThanMax?: boolean;
	lowerThanMin?: boolean;
}

@Directive({
	selector: 'input[type="number2"]',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => Number2Directive),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => Number2Directive),
			multi: true,
		},
	],
})
export class Number2Directive extends NumberValueAccessor implements Validator, ControlValueAccessor {
	private maxValue: number | undefined;
	private minValue: number | undefined;
	private stepValue = 1;
	private isDisabled: boolean | undefined;
	private isFocussed: boolean;
	private distinguishNonNumericEmpty: boolean;

	@Input()
	set max(value: string | number | undefined) {
		this.maxValue = Number(value);
	}

	@Input()
	set min(value: string | number | undefined) {
		this.minValue = Number(value);
	}

	@Input()
	set step(value: string | undefined) {
		this.stepValue = Number(value) || 1;
	}

	@Input()
	set distinguishNonNumericFromEmpty(value: boolean | undefined) {
		this.distinguishNonNumericEmpty = value || false;
	}

	@HostListener("input", ["$event"])
	onInput($event: any) {
		const rawValue = $event.target.value;
		const normalizedValue = rawValue.replace(/,/g, ".");
		const value = Number(normalizedValue);

		// For some inputs we might want to distinguish between empty values and non numeric ones, for example if the user is allowed to empty the input field.
		if (this.distinguishNonNumericEmpty && rawValue === "") {
			this.onChange(null);
		} else if (this.distinguishNonNumericEmpty && (normalizedValue.endsWith(".") || Number.isNaN(value))) {
			this.onChange(NaN);
		} else {
			if (rawValue === "" || normalizedValue.endsWith(".") || Number.isNaN(value)) {
				this.onChange("");
			} else {
				this.onChange(Number(value.toFixed(10)));
			}
		}
	}

	@HostListener("keydown", ["$event"])
	onKeyDown($event: any) {
		if (CONTROL_KEYS.has($event.key)) {
			return this.handleControlKeys($event);
		}

		if ($event.key.length > 1 || $event.metaKey || $event.ctrlKey) {
			// Allow every key which has a length greater than 1 (which means its a special key like `Tab`) or if the meta key is pressed or if the control key is pressed (for copy and paste functionality).
			return true;
		}

		if ($event.target.selectionStart === 0) {
			if ($event.target.selectionEnd === 0 && /^[+-]{1}/.test($event.target.value)) {
				// Don't allow input when cursor is at the start of the field and we already have a sign character
				return false;
			}

			// eslint-disable-next-line no-useless-escape
			if ($event.target.selectionEnd > 0 || /^[\d\.,]*$/.test($event.target.value)) {
				// Allow sign character if the first X characters are selected or if we only have digits in our input field
				// eslint-disable-next-line no-useless-escape
				return /^[+\-\d\.,]$/.test($event.key);
			}
		}

		// eslint-disable-next-line no-useless-escape
		return /^[\d\.,]$/.test($event.key);
	}

	@HostListener("wheel", ["$event"])
	@HostListener("mousewheel", ["$event"])
	@HostListener("DOMMouseScroll", ["$event"])
	@HostListener("onmousewheel", ["$event"])
	onMouseWheel(event: any) {
		if (event.preventDefault) {
			event.preventDefault();
		}
	}

	@HostListener("focus")
	onFocus() {
		this.isFocussed = true;
	}

	@HostListener("blur")
	onBlur() {
		this.isFocussed = false;
		this.onTouched();
	}

	constructor() {
		const renderer = inject(Renderer2);
		const elementRef = inject(ElementRef);

		super(renderer, elementRef);
	}

	validate(control: AbstractControl): Number2DirectiveValidationErrors | null {
		const value = Number(control.value);

		if (Number.isNaN(value)) {
			return {
				invalidNumber: true,
			};
		}
		if (isNumber(this.maxValue) && value > this.maxValue) {
			return {
				greaterThanMax: true,
			};
		}
		if (isNumber(this.minValue) && value < this.minValue) {
			return {
				lowerThanMin: true,
			};
		}

		return null;
	}

	setDisabledState(isDisabled: boolean): void {
		super.setDisabledState(isDisabled);

		this.isDisabled = isDisabled;
	}

	private changeValue(event: KeyboardEvent, operation: "increase" | "decrease"): void {
		const value = Number((event.target as HTMLInputElement).value.replace(",", ".")) || 0;

		let result = value;

		if (operation === "increase") {
			result = value + this.stepValue;
		} else if (operation === "decrease") {
			result = value - this.stepValue;
		}

		if (isNumber(this.maxValue) && result > this.maxValue) {
			this.setValue(event, this.maxValue);
		} else if (isNumber(this.minValue) && result < this.minValue) {
			this.setValue(event, this.minValue);
		} else {
			this.setValue(event, result);
		}
	}

	private increaseValue(event): void {
		this.changeValue(event, "increase");
	}

	private decreaseValue(event): void {
		this.changeValue(event, "decrease");
	}

	private setValue(event, value: number): void {
		if (this.isDisabled) {
			return;
		}

		const hasComma = event.target.value.includes(",");

		if (Number(event.target.value.replace(",", ".")) === value) {
			return;
		}

		const numericValue = Number(value.toFixed(10));

		event.target.value = hasComma ? `${numericValue}`.replace(".", ",") : numericValue;

		const ev = document.createEvent("Event");
		ev.initEvent("input", false, false);
		event.target.dispatchEvent(ev);
	}

	private handleControlKeys(event: KeyboardEvent) {
		switch (event.key) {
			case "ArrowUp":
			case "Up":
				this.increaseValue(event);
				break;
			case "ArrowDown":
			case "Down":
				this.decreaseValue(event);
				break;
			default:
				return;
		}
	}
}
